// ChatMessages.js
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useOutletContext, useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useThreadApi } from '../../Services/ThreadApiProvider';
import Button from '../Buttons';
import { ConfirmationProvider, useConfirmation } from '../ConfirmationContext';
import SureLoader from '../SureLoader';
import ChatInputContainer from './ChatInputContainer'; // <--- import here
import TrainPopupModal from './TrainPopupModal';
import './TrainPopupModal.css';

import AudioMessagesPlayer from './AudioMessagesPlayer';
import { FileAttachment } from './FileAttachment';
import ImageGallery from './ImageGallery';

import { useAuth } from '../../Contexts/AuthContext';
import { useSimulationDatasetApi } from '../../Services/SimulationDatasetApiProvider';
import { useAlert } from '../AlertContext';
import ItemsPickerModal from '../ItemsPickerModal';
import { MessageComposeToolbar } from './MessageAttachmentsEditor';
import {
  AssistantHeaderMessage,
  MediaMessageBotAudio,
  MediaMessageBotFile,
  MediaMessageBotHTML,
  MediaMessageBotImage,
  MediaMessageBotURL,
  MediaMessageBotVideo,
  MediaMessageUserAudio,
  MediaMessageUserFile,
  MediaMessageUserHTML,
  MediaMessageUserImage,
  MediaMessageUserURL,
  MediaMessageUserVideo,
} from './MessageComponents';
import MessageMetaBlocks from './MessageMetaBlock';
import MessageReactions from './MessageReactions';
import PostCallAnalysisCard from './PostCallAnalysisCard';
import TypingIndicator from './TypingIndicator';

const ChatMessagesContent = () => {
  const { organizationId, chatId } = useParams();
  const datasetPickerColumns = [
    {
      key: 'name',
      header: 'Dataset',
      render: (dataset) => dataset.name,
    },
  ];
  const { status } = useAuth();
  const { showAlert } = useAlert();
  const { getDatasets, importConversation } = useSimulationDatasetApi();
  const userId = status.id;

  const { canEdit } = useOutletContext();
  const { showConfirmation } = useConfirmation();
  const { isTyping } = useThreadApi();
  const messageRefs = useRef({});
  const [replyLoading, setReplyLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [moderateLoading, setModerateLoading] = useState(false);
  const [virtualLoading, setVirtualLoading] = useState(false);
  const [trainMessageId, setTrainMessageId] = useState(null);
  const [showTrainModal, setShowTrainModal] = useState(false);

  const [collapsedSystemById, setCollapsedSystemById] = useState({});
  const [datasets, setDatasets] = useState([]);
  const [isDatasetPickerOpen, setIsDatasetPickerOpen] = useState(false);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [chatImporting, setChatImporting] = useState(false);

  const openTrainModal = (msgId) => {
    setTrainMessageId(msgId);
    setShowTrainModal(true);
  };

  const closeTrainModal = () => {
    setTrainMessageId(null);
    setShowTrainModal(false);
  };

  const onTrainDone = async () => {
    // TODO: Handle apply
    //await applyMessage(trainMessageId, updatedFrame); // mimic `onApplyMessage`
    closeTrainModal();
  };

  const {
    thread,
    threadMessages,
    loadingMessages,
    closeThread,
    setVirtualThread,
    setModeratedThread,
    replyToThreadWithAi,
    peekThreadFromQueue,
    deleteMessage,
    applyMessage,
    analysisResults,
    loadingAnalysis,
    setMessageReaction,
    deleteMessageReaction,
  } = useThreadApi();

  const allowedReactions = thread?.channel?.allowed_reactions;
  const hasAllowedReactions = Array.isArray(allowedReactions) && allowedReactions.length > 0;
  const canImportToDataset = status.permissions?.includes('all:assistant') === true;

  const listKey = `${organizationId || ''}-${chatId || ''}-${thread?.id || ''}`;

  // formatPhoneNumber function for phones of any country:
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 6) return phone; // too short to format

    let countryCode = '';
    let rest = digits;

    if (digits.startsWith('0')) {
      // Local number without country code
      return phone;
    } else if (digits.startsWith('00')) {
      // International prefix with 00
      countryCode = '+' + digits.slice(2, 4);
      rest = digits.slice(4);
    } else if (digits.startsWith('+')) {
      // Already in + format (rare in raw digits form)
      return phone;
    } else if (digits.length > 10) {
      // Assume first 1–3 digits are country code
      countryCode = '+' + digits.slice(0, digits.length - 10);
      rest = digits.slice(digits.length - 10);
    }

    // Format rest as (XXX) XXX XX XX or similar depending on length
    if (rest.length === 10) {
      return `${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)} ${rest.slice(6, 8)} ${rest.slice(8)}`;
    } else if (rest.length === 9) {
      return `${countryCode} (${rest.slice(0, 2)}) ${rest.slice(2, 5)} ${rest.slice(5, 7)} ${rest.slice(7)}`;
    }

    return `${countryCode} ${rest}`;
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const msgDate = new Date(timestamp);
    if (isNaN(msgDate.getTime())) return 'Invalid date';

    const isToday = now.toDateString() === msgDate.toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = yesterday.toDateString() === msgDate.toDateString();
    const hours = String(msgDate.getHours()).padStart(2, '0');
    const minutes = String(msgDate.getMinutes()).padStart(2, '0');

    if (isToday) {
      return `${hours}:${minutes}`;
    } else if (isYesterday) {
      return `Yesterday ${hours}:${minutes}`;
    } else {
      const month = msgDate.toLocaleString('default', { month: 'short' });
      const day = msgDate.getDate();
      return `${month} ${day}, ${hours}:${minutes}`;
    }
  };

  const extractToolName = (message) => {
    const meta = message.message_metadata || message.assistant_metadata || {};

    if (typeof meta.tool_name === 'string' && meta.tool_name.trim()) {
      return meta.tool_name.trim();
    }

    const text =
      typeof message.text === 'string' ? message.text : JSON.stringify(message.text ?? '', null, 2);
    const match = text.match(/Tool name:\s*([^\n\r]+)/i);
    return match?.[1]?.trim() || null;
  };

  const onSetVirtualThread = async (virtual) => {
    setVirtualLoading(true);
    try {
      await setVirtualThread(virtual);
    } finally {
      setVirtualLoading(false);
    }
  };

  const onSetModeratedThread = async (moderated) => {
    setModerateLoading(true);
    try {
      await setModeratedThread(moderated);
    } finally {
      setModerateLoading(false);
    }
  };

  const onCloseChat = async () => {
    showConfirmation('Ви впевнені, що хочете закрити чат?', async () => {
      setCloseLoading(true);
      try {
        await closeThread();
      } finally {
        setCloseLoading(false);
      }
    });
  };

  const onReplyToThreadWithAi = async () => {
    setReplyLoading(true);
    try {
      await replyToThreadWithAi();
    } finally {
      setReplyLoading(false);
    }
  };

  const onPeekThreadFromQueue = async () => {
    setReplyLoading(true);
    try {
      await peekThreadFromQueue();
    } finally {
      setReplyLoading(false);
    }
  };

  async function onDeleteMessage(id) {
    await deleteMessage(id);
  }

  // Local input state
  const [messageText, setMessageText] = useState('');
  const [editedTexts, setEditedTexts] = useState({});
  const [editedImagesByMessage, setEditedImagesByMessage] = useState({});
  const [editedFilesByMessage, setEditedFilesByMessage] = useState({});
  const [editedAudioByMessage, setEditedAudioByMessage] = useState({});

  const [draftNewUploadsByMessage, setDraftNewUploadsByMessage] = useState({});

  async function onApplyMessage(id, text, attachments, newUploadsPayload) {
    await applyMessage(id, text, attachments, newUploadsPayload);
  }

  function handleImagesChange(messageId, newVisibleKeys) {
    setEditedImagesByMessage((prev) => ({
      ...prev,
      [messageId]: newVisibleKeys,
    }));
  }

  function handleRemoveFile(messageId, fileKeyToRemove) {
    setEditedFilesByMessage((prev) => {
      const prevFiles =
        prev[messageId] ?? (threadMessages.find((m) => m.id === messageId)?.files || []);
      const newFiles = prevFiles.filter((f) => f.key !== fileKeyToRemove);

      return {
        ...prev,
        [messageId]: newFiles,
      };
    });
  }

  function handleRemoveAudio(messageId, audioKeyToRemove) {
    setEditedAudioByMessage((prev) => {
      const originalAudios = threadMessages.find((m) => m.id === messageId)?.audio || [];

      const prevAudios = prev[messageId] ?? originalAudios;

      const newAudios = prevAudios.filter((a) => a.key !== audioKeyToRemove);

      return {
        ...prev,
        [messageId]: newAudios,
      };
    });
  }

  function addEmojiBeforeClosingBold(text, emoji) {
    if (/\*\*\s*$/.test(text)) {
      return text.replace(/\s*\*\*\s*$/, `${emoji}**`);
    }
    return `${text}${emoji}`;
  }

  const handleAddEmojiToDraft = (messageId, emojiStr) => {
    setEditedTexts((prev) => {
      const oldVal =
        prev[messageId] !== undefined
          ? prev[messageId]
          : (() => {
              const msg = threadMessages.find((m) => m.id === messageId);
              if (!msg) return '';
              return typeof msg.text === 'string' ? msg.text : JSON.stringify(msg.text, null, 2);
            })();

      return {
        ...prev,
        [messageId]: addEmojiBeforeClosingBold(oldVal || '', emojiStr),
      };
    });
  };

  const handleAddFilesToDraft = (messageId, newFilesArr) => {
    setDraftNewUploadsByMessage((prev) => {
      const oldArr = prev[messageId] || [];
      return {
        ...prev,
        [messageId]: [...oldArr, ...newFilesArr],
      };
    });
  };

  // For autoscrolling to bottom
  const messagesEndRef = useRef(null);

  // Scroll to bottom whenever new messages or the typing indicator changes
  useEffect(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    });
  }, [threadMessages, isTyping]);

  useEffect(() => {
    setMessageText('');
  }, [thread]);

  const openDatasetPicker = () => {
    setIsDatasetPickerOpen(true);
    setDatasetsLoading(true);

    getDatasets(0, 1000)
      .then(
        (response) => {
          setDatasets(response.items.filter((dataset) => dataset.is_active));
        },
        () => {
          showAlert('fail', 'Failed to load simulation datasets');
        },
      )
      .finally(() => setDatasetsLoading(false));
  };

  const importChatToDataset = (selectedDatasets) => {
    const selectedDataset = selectedDatasets[0];

    if (!selectedDataset) return;

    setChatImporting(true);
    importConversation(selectedDataset.key, thread.id)
      .then(
        () => {
          setIsDatasetPickerOpen(false);
          showAlert('success', `Chat imported to ${selectedDataset.name}`);
        },
        () => {
          showAlert('fail', 'Failed to import chat');
        },
      )
      .finally(() => setChatImporting(false));
  };

  return (
    <div className="chat-container">
      {/* Header area, if desired */}
      <div className={`chat-header ${thread?.is_active === true ? 'closed-chat' : ''}`}>
        <div className="chat-info">
          {thread && (
            <>
              <div
                style={thread?.starterImg ? { backgroundImage: `url(${thread.starterImg})` } : {}}
                className={`chat-logo ${thread?.starterImg ? '' : 'user-logo'}`}
              ></div>
              <div className="chat-creds">
                <h4>
                  {thread.name} {thread.is_waiting && '( Queue )'}
                </h4>
                <div className="phone-nuber">{formatPhoneNumber(thread.phone)}</div>

                <span>{thread.email}</span>
              </div>
            </>
          )}
        </div>
        {thread && (
          <div className="chat-controls">
            {canImportToDataset && (
              <Button
                variant="ghost"
                size="medium"
                onClick={openDatasetPicker}
                disabled={chatImporting}
                className={chatImporting ? 'loader' : ''}
              >
                Import to dataset
              </Button>
            )}
            {thread.is_active === true && canEdit && (
              <>
                {thread.is_virtual === true ? <div className="assistant-logo"></div> : ''}
                {thread.is_waiting === true && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={onPeekThreadFromQueue}
                    disabled={replyLoading}
                    className={replyLoading ? 'loader' : ''}
                  >
                    Peek From Queue
                  </Button>
                )}
                {thread.is_virtual === false && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={onReplyToThreadWithAi}
                    disabled={replyLoading}
                    className={replyLoading ? 'loader' : ''}
                  >
                    Enable AI + Reply
                  </Button>
                )}
                {thread.is_virtual === false && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={() => onSetVirtualThread(true)}
                    disabled={virtualLoading}
                    className={virtualLoading ? 'loader' : ''}
                  >
                    Enable AI
                  </Button>
                )}
                {thread.is_virtual === true && thread.is_moderated === false && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={() => onSetModeratedThread(true)}
                    disabled={moderateLoading}
                    className={moderateLoading ? 'loader' : ''}
                  >
                    Enable Moderation
                  </Button>
                )}
                {thread.is_virtual === true && thread.is_moderated === true && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={() => onSetModeratedThread(false)}
                    disabled={moderateLoading}
                    className={moderateLoading ? 'loader' : ''}
                  >
                    Disable Moderation
                  </Button>
                )}
                {thread.is_virtual === true && (
                  <Button
                    variant="ghost"
                    size="medium"
                    onClick={() => onSetVirtualThread(false)}
                    disabled={virtualLoading}
                    className={virtualLoading ? 'loader' : ''}
                  >
                    Take Over Chat
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="medium"
                  onClick={onCloseChat}
                  disabled={closeLoading}
                  className={closeLoading ? 'loader' : ''}
                >
                  End Chat
                </Button>
              </>
            )}
            {thread.is_active === false && <div className="closed-chat-message">Chat Closed</div>}
          </div>
        )}
      </div>

      {/* Post-call analysis results */}
      {thread && !thread.is_active && !loadingAnalysis && analysisResults && (
        <PostCallAnalysisCard analysisResult={analysisResults} />
      )}

      {/* Main messages area with fancy rendering */}
      <div key={listKey} className="chat-messages">
        <AnimatePresence key={listKey} initial={false}>
          {loadingMessages ? (
            <SureLoader />
          ) : (
            threadMessages.map((msg, index) => {
              // ────────────────
              // CASE 1: Assistant Header Message (intro)
              // ────────────────
              if (msg.newassistant) {
                return (
                  <React.Fragment key={msg.id}>
                    <AssistantHeaderMessage message={msg} />
                  </React.Fragment>
                );
              }

              // ────────────────
              // CASE 2: System Message
              // ────────────────
              const isSystem = msg.sender === 'system';

              if (isSystem) {
                const meta = msg.message_metadata || msg.assistant_metadata || {};
                const thematics = Array.isArray(msg.thematics) ? msg.thematics : [];
                const triggers = Array.isArray(msg.triggers) ? msg.triggers : [];
                const guardrails = Array.isArray(msg.guardrails) ? msg.guardrails : [];
                const webs = Array.isArray(meta.url_annotations) ? meta.url_annotations : [];
                const filesMeta = Array.isArray(meta.file_annotations) ? meta.file_annotations : [];
                const frames = Array.isArray(meta.frames) ? meta.frames : [];
                const notificationsList = Array.isArray(meta.notifications)
                  ? meta.notifications
                  : [];
                const connectionObjectsList = Array.isArray(meta.connection_objects)
                  ? meta.connection_objects
                  : [];
                const fileSearchQueries = Array.isArray(meta.file_search_queries)
                  ? meta.file_search_queries
                  : [];
                const reasoningSteps = Array.isArray(meta.reasoning_steps)
                  ? meta.reasoning_steps
                  : [];

                let formattedText =
                  typeof msg.text === 'string' ? msg.text : JSON.stringify(msg.text, null, 2);
                const isCollapsed = collapsedSystemById[msg.id] ?? true;
                const toolName = extractToolName(msg);

                return (
                  <div layout="position" key={msg.id || index} className="message-row system">
                    <motion.div
                      className={`message-bubble system-msg${isCollapsed ? ' is-collapsed' : ''}`}
                      style={{ position: 'relative' }}
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        duration: 0.1,
                      }}
                    >
                      <a
                        href="#"
                        className="system-msg__toggle"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 12,
                          fontSize: '12px',
                          zIndex: 2,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCollapsedSystemById((prev) => ({
                            ...prev,
                            [msg.id]: !(prev[msg.id] ?? true),
                          }));
                        }}
                      >
                        {isCollapsed ? 'Read more' : 'Show less'}
                      </a>

                      <div
                        className="system-msg__content"
                        style={{
                          position: 'relative',
                          maxHeight: isCollapsed ? '6em' : 'none',
                          overflow: 'hidden',
                          paddingRight: '88px',
                          WebkitMaskImage: isCollapsed
                            ? 'linear-gradient(to bottom, #000 55%, rgba(0,0,0,0) 100%)'
                            : 'none',
                          maskImage: isCollapsed
                            ? 'linear-gradient(to bottom, #000 55%, rgba(0,0,0,0) 100%)'
                            : 'none',
                          filter: isCollapsed ? 'none' : 'none',
                        }}
                      >
                        <div>
                          <span style={{ fontSize: '12px' }}>{formatTimestamp(msg.timestamp)}</span>
                          {meta.run_key && (msg.assistant_key || thread?.channel?.assistant_id) && (
                            <a
                              href={`/organizations/${organizationId}/agents-hub/${msg.assistant_key || thread?.channel?.assistant_id}/audit-operations/${meta.run_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', marginLeft: '8px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {meta.run_key}
                            </a>
                          )}
                        </div>

                        <div style={{ fontWeight: 600, marginTop: 4 }}>Agent Actions:</div>
                        {toolName ? <div>Tool name: {toolName}</div> : null}

                        {msg.images && msg.images.length > 0 && <ImageGallery message={msg} />}

                        {msg.audio?.[0] && (
                          <AudioMessagesPlayer
                            audioKey={msg.audio[0].key}
                            histogramData={msg.audio[0].histogram || []}
                            playbackTime={msg.audio[0].playback_time}
                          />
                        )}

                        {Array.isArray(msg.files) &&
                          msg.files.length > 0 &&
                          msg.files.map((file, i) => (
                            <FileAttachment key={file.key || i} file={file} />
                          ))}

                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            a: ({ node, ...props }) => (
                              <a {...props} target="_blank" rel="noopener noreferrer">
                                {props.children}
                              </a>
                            ),
                          }}
                        >
                          {formattedText}
                        </ReactMarkdown>
                      </div>

                      {hasAllowedReactions && (
                        <MessageReactions
                          messageId={msg.id}
                          reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                          allowedReactions={allowedReactions}
                          currentUserNativeId={userId}
                          currentReactorType="user"
                          onSetReaction={(reaction) => setMessageReaction(msg.id, reaction)}
                          onDeleteReaction={(reaction) => deleteMessageReaction(msg.id, reaction)}
                        />
                      )}

                      <MessageMetaBlocks
                        organizationId={organizationId}
                        thematics={thematics}
                        triggers={triggers}
                        guardrails={guardrails}
                        webs={webs}
                        filesMeta={filesMeta}
                        frames={frames}
                        notificationsList={notificationsList}
                        fileSearchQueries={fileSearchQueries}
                        connectioObjectsList={connectionObjectsList}
                        reasoningSteps={reasoningSteps}
                      />
                    </motion.div>
                  </div>
                );
              }

              // ────────────────
              // Compute grouping information for all messages
              // ────────────────
              const isAssistant = msg.sender === 'assistant';

              // Check previous and next messages
              const prevMsg = threadMessages[index - 1];
              const nextMsg = threadMessages[index + 1];

              const isFirstAssistantMsg =
                isAssistant && (!prevMsg || prevMsg.sender !== 'assistant');
              const isLastAssistantMsg =
                isAssistant && (!nextMsg || nextMsg.sender !== 'assistant');
              const isMiddleAssistantMsg =
                isAssistant && !isFirstAssistantMsg && !isLastAssistantMsg;
              const isOnlyAssistantMsg = isFirstAssistantMsg && isLastAssistantMsg;

              const isUser = msg.sender === 'user';
              const isFirstUserMsg = isUser && (!prevMsg || prevMsg.sender !== 'user');
              const isLastUserMsg = isUser && (!nextMsg || nextMsg.sender !== 'user');
              const isMiddleUserMsg = isUser && !isFirstUserMsg && !isLastUserMsg;
              const isOnlyUserMsg = isFirstUserMsg && isLastUserMsg;

              const groupingClasses = [
                isAssistant ? 'assistant' : 'user',

                // Assistant message grouping
                isOnlyAssistantMsg
                  ? 'only-assistant-msg'
                  : isFirstAssistantMsg
                    ? 'first-assistant-msg'
                    : isMiddleAssistantMsg
                      ? 'middle-assistant-msg'
                      : isLastAssistantMsg
                        ? 'last-assistant-msg'
                        : '',

                // User message grouping
                isOnlyUserMsg
                  ? 'only-user-msg'
                  : isFirstUserMsg
                    ? 'first-user-msg'
                    : isMiddleUserMsg
                      ? 'middle-user-msg'
                      : isLastUserMsg
                        ? 'last-user-msg'
                        : '',
              ]
                .filter(Boolean)
                .join(' ');

              // ────────────────
              // Determine inner content:
              // If msg.typeOfMedia exists and is not "simple_text", select the proper media component;
              // otherwise, use the simple text markup.
              // ────────────────
              const meta = msg.message_metadata || msg.assistant_metadata || {};
              const thematics = Array.isArray(msg.thematics) ? msg.thematics : [];
              const triggers = Array.isArray(msg.triggers) ? msg.triggers : [];
              const guardrails = Array.isArray(msg.guardrails) ? msg.guardrails : [];
              const webs = Array.isArray(meta.url_annotations) ? meta.url_annotations : [];
              const filesMeta = Array.isArray(meta.file_annotations) ? meta.file_annotations : [];
              const frames = Array.isArray(meta.frames) ? meta.frames : [];
              const notificationsList = Array.isArray(meta.notifications) ? meta.notifications : [];
              const connectionObjectsList = Array.isArray(meta.connection_objects)
                ? meta.connection_objects
                : [];
              const fileSearchQueries = Array.isArray(meta.file_search_queries)
                ? meta.file_search_queries
                : [];
              const reasoningSteps = Array.isArray(meta.reasoning_steps)
                ? meta.reasoning_steps
                : [];

              const audioItem = msg.audio?.[0] || null;
              const fileItems = Array.isArray(msg.files) ? msg.files : [];

              let innerContent;

              if (msg.typeOfMedia && msg.typeOfMedia !== 'simple_text') {
                if (msg.sender === 'user') {
                  switch (msg.typeOfMedia) {
                    case 'url':
                      innerContent = <MediaMessageBotURL message={msg} />;
                      break;
                    case 'image':
                      innerContent = <MediaMessageBotImage message={msg} />;
                      break;
                    case 'audio':
                      innerContent = <MediaMessageBotAudio message={msg} />;
                      break;
                    case 'file':
                      innerContent = <MediaMessageBotFile message={msg} />;
                      break;
                    case 'video':
                      innerContent = <MediaMessageBotVideo message={msg} />;
                      break;
                    case 'HTML':
                      innerContent = <MediaMessageBotHTML message={msg} />;
                      break;
                    default:
                      innerContent = null;
                  }
                } else if (msg.sender === 'assistant') {
                  switch (msg.typeOfMedia) {
                    case 'url':
                      innerContent = <MediaMessageUserURL message={msg} />;
                      break;
                    case 'image':
                      innerContent = <MediaMessageUserImage message={msg} />;
                      break;
                    case 'audio':
                      innerContent = <MediaMessageUserAudio message={msg} />;
                      break;
                    case 'file':
                      innerContent = <MediaMessageUserFile message={msg} />;
                      break;
                    case 'video':
                      innerContent = <MediaMessageUserVideo message={msg} />;
                      break;
                    case 'HTML':
                      innerContent = <MediaMessageUserHTML message={msg} />;
                      break;
                    default:
                      innerContent = null;
                  }
                }
              } else {
                // Simple text message markup
                let formattedText =
                  typeof msg.text === 'string' ? msg.text : JSON.stringify(msg.text, null, 2);

                innerContent = (
                  <>
                    {msg.images && msg.images.length > 0 && <ImageGallery message={msg} />}
                    {audioItem && (
                      <AudioMessagesPlayer
                        audioKey={audioItem.key}
                        histogramData={audioItem.histogram || []}
                        playbackTime={audioItem.playback_time}
                      />
                    )}
                    {fileItems &&
                      fileItems.length > 0 &&
                      fileItems.map((file, i) => (
                        <FileAttachment key={file.key || i} file={file} />
                      ))}
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                          </a>
                        ),
                      }}
                    >
                      {formattedText}
                    </ReactMarkdown>
                    {/*<MessageMetaBlocks*/}
                    {/*  organizationId={organizationId}*/}
                    {/*  thematics={thematics}*/}
                    {/*  triggers={triggers}*/}
                    {/*  guardrails={guardrails}*/}
                    {/*  webs={webs}*/}
                    {/*  filesMeta={filesMeta}*/}
                    {/*  frames={frames}*/}
                    {/*  notificationsList={notificationsList}*/}
                    {/*  fileSearchQueries={fileSearchQueries}*/}
                    {/*  connectioObjectsList={connectionObjectsList}*/}
                    {/*  reasoningSteps={reasoningSteps}*/}
                    {/*/>*/}
                  </>
                );
              }

              return (
                <div
                  layout="position"
                  key={msg.id || index}
                  className={`message-row ${groupingClasses} ${(isUser && !isLastUserMsg) || (isAssistant && !isLastAssistantMsg) ? 'no-badge' : ''}`}
                >
                  {/* Avatar for user messages (shown only for the last message in a sequence) */}
                  {!isAssistant && isLastUserMsg && (
                    <motion.div
                      className={`message-avatar ${thread?.starterImg ? '' : 'user-logo'}`}
                      style={
                        thread?.starterImg ? { backgroundImage: `url(${thread.starterImg})` } : {}
                      }
                      initial={{
                        // opacity: 0,
                        scale: 0.5,
                      }}
                      animate={{
                        // opacity: 1,
                        scale: 1,
                      }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        duration: 0.1,
                      }}
                    />
                  )}

                  {/* The bubble */}
                  <motion.div
                    className={`message-bubble ${isAssistant ? 'assistant-msg' : 'user-msg'}
                        ${['bot', 'automation'].includes(msg.type) ? 'bot' : 'human'}
                        ${msg.isSent === false ? 'moderated' : ''}`}
                    initial={{
                      // opacity: 0,
                      scale: 0.9,
                      x: isAssistant ? -10 : 10,
                    }}
                    animate={{
                      // opacity: 1,
                      scale: 1,
                      x: 0,
                    }}
                    exit={{ opacity: 0, scale: 0.9, x: isAssistant ? -10 : 10 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                      duration: 0.1,
                      // delay: index * 0.02
                    }}
                  >
                    {/* {innerContent} */}
                    {msg.isSent === false ? (
                      <>
                        <span style={{ fontSize: '12px' }}>{formatTimestamp(msg.timestamp)}</span>
                        {isAssistant &&
                          meta.run_key &&
                          (msg.assistant_key || thread?.channel?.assistant_id) && (
                            <a
                              href={`/organizations/${organizationId}/agents-hub/${msg.assistant_key || thread?.channel?.assistant_id}/audit-operations/${meta.run_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', marginLeft: '8px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {meta.run_key}
                            </a>
                          )}

                        {msg.images && msg.images.length > 0 && (
                          <ImageGallery
                            message={msg}
                            isEditable={msg.isSent === false}
                            onImagesChange={handleImagesChange}
                          />
                        )}

                        {(() => {
                          const currentAudios =
                            editedAudioByMessage[msg.id] ??
                            (Array.isArray(msg.audio) ? msg.audio : []);

                          if (!currentAudios || currentAudios.length === 0) return null;

                          return currentAudios.map((audio, idx) => (
                            <AudioMessagesPlayer
                              key={audio.key || idx}
                              audioKey={audio.key}
                              histogramData={audio.histogram || []}
                              playbackTime={audio.playback_time}
                              onDelete={(key) => handleRemoveAudio(msg.id, key)}
                            />
                          ));
                        })()}

                        {(() => {
                          const currentFiles =
                            editedFilesByMessage[msg.id] ??
                            (Array.isArray(msg.files) ? msg.files : []);

                          if (!currentFiles || currentFiles.length === 0) return null;

                          return currentFiles.map((file, i) => (
                            <FileAttachment
                              key={file.key || i}
                              file={file}
                              onDelete={(key) => handleRemoveFile(msg.id, key)}
                            />
                          ));
                        })()}
                        <textarea
                          data-id={msg.id}
                          className="message-edit-textarea"
                          value={
                            editedTexts[msg.id] !== undefined
                              ? editedTexts[msg.id]
                              : typeof msg.text === 'string'
                                ? msg.text
                                : JSON.stringify(msg.text, null, 2)
                          }
                          style={{ overflow: 'hidden', resize: 'none' }}
                          ref={(el) => {
                            if (el) {
                              el.style.height = 'auto';
                              el.style.height = `${el.scrollHeight}px`;
                              messageRefs.current[msg.id] = el;
                            }
                          }}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                            setEditedTexts((prev) => ({ ...prev, [msg.id]: e.target.value }));
                          }}
                        />

                        {hasAllowedReactions && (
                          <MessageReactions
                            messageId={msg.id}
                            reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                            allowedReactions={allowedReactions}
                            currentUserNativeId={userId}
                            currentReactorType="user"
                            onSetReaction={(reaction) => setMessageReaction(msg.id, reaction)}
                            onDeleteReaction={(reaction) => deleteMessageReaction(msg.id, reaction)}
                          />
                        )}

                        <MessageMetaBlocks
                          organizationId={organizationId}
                          thematics={thematics}
                          triggers={triggers}
                          guardrails={guardrails}
                          webs={webs}
                          filesMeta={filesMeta}
                          frames={frames}
                          notificationsList={notificationsList}
                          fileSearchQueries={fileSearchQueries}
                          connectioObjectsList={connectionObjectsList}
                        />

                        <div className="draft-compose-toolbar-row">
                          <MessageComposeToolbar
                            onEmoji={(emojiStr) => handleAddEmojiToDraft(msg.id, emojiStr)}
                            onAddFiles={(newFilesArr) => handleAddFilesToDraft(msg.id, newFilesArr)}
                            wrapperClassName="chat-options"
                            emojiButtonClassName="emozy-button"
                            imageButtonClassName="attach-file-button image-uploader"
                            fileButtonClassName="attach-file-button"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '12px' }}>{formatTimestamp(msg.timestamp)}</span>
                        {isAssistant &&
                          meta.run_key &&
                          (msg.assistant_key || thread?.channel?.assistant_id) && (
                            <a
                              href={`/organizations/${organizationId}/agents-hub/${msg.assistant_key || thread?.channel?.assistant_id}/audit-operations/${meta.run_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', marginLeft: '8px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {meta.run_key}
                            </a>
                          )}

                        {innerContent}

                        {hasAllowedReactions && (
                          <MessageReactions
                            messageId={msg.id}
                            reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                            allowedReactions={allowedReactions}
                            currentUserNativeId={userId}
                            currentReactorType="user"
                            onSetReaction={(reaction) => setMessageReaction(msg.id, reaction)}
                            onDeleteReaction={(reaction) => deleteMessageReaction(msg.id, reaction)}
                          />
                        )}

                        <MessageMetaBlocks
                          organizationId={organizationId}
                          thematics={thematics}
                          triggers={triggers}
                          guardrails={guardrails}
                          webs={webs}
                          filesMeta={filesMeta}
                          frames={frames}
                          notificationsList={notificationsList}
                          fileSearchQueries={fileSearchQueries}
                          connectioObjectsList={connectionObjectsList}
                          reasoningSteps={reasoningSteps}
                        />
                      </>
                    )}
                    {msg.isSent === false && (
                      <div className="buttons-panel">
                        <Button
                          variant="danger"
                          size="small"
                          className="remove"
                          onClick={() => onDeleteMessage(msg.id)}
                        >
                          Delete
                        </Button>

                        <Button
                          variant="ghost"
                          size="small"
                          className="apply"
                          onClick={async () => {
                            const value = messageRefs.current[msg.id]?.value ?? msg.text;

                            const finalImages =
                              editedImagesByMessage[msg.id] ??
                              (Array.isArray(msg.images) ? msg.images : []);

                            const currentFiles =
                              editedFilesByMessage[msg.id] ??
                              (Array.isArray(msg.files) ? msg.files : []);

                            const finalFileKeys = currentFiles.map((f) => f.key);

                            const finalAudios =
                              editedAudioByMessage[msg.id] ??
                              (Array.isArray(msg.audio) ? msg.audio : []);

                            const extraDraftUploads = draftNewUploadsByMessage[msg.id] || [];

                            const attachmentsPayload = {
                              images: finalImages,
                              files: finalFileKeys,
                              audios: finalAudios,
                            };

                            const newUploadsPayload = extraDraftUploads.map((f) => ({
                              file: f.file,
                              name: f.name,
                              extension: f.extension,
                              sizeMB: f.sizeMB,
                              base64: f.base64Preview || f.base64File || null,
                              type: f.base64Preview ? 'image' : 'file',
                            }));

                            await onApplyMessage(
                              msg.id,
                              value,
                              attachmentsPayload,
                              newUploadsPayload,
                            );

                            setDraftNewUploadsByMessage((prev) => {
                              const copy = { ...prev };
                              delete copy[msg.id];
                              return copy;
                            });
                          }}
                        >
                          Apply
                        </Button>
                        {msg.tags && msg.tags.length > 0 && msg.tags.some((t) => t === 'FRAME') && (
                          <Button
                            variant="ghost"
                            size="small"
                            className="modify"
                            disabled={showTrainModal}
                            onClick={() => openTrainModal(msg.id)}
                          >
                            Train
                          </Button>
                        )}

                        {showTrainModal && trainMessageId === msg.id && (
                          <TrainPopupModal
                            onClose={closeTrainModal}
                            onTrainDone={onTrainDone}
                            messageId={msg.id}
                          />
                        )}
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })
          )}
        </AnimatePresence>
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {thread && thread.is_active === true && (
        <ChatInputContainer key={thread.id} content={messageText} />
      )}

      <ItemsPickerModal
        isOpen={isDatasetPickerOpen}
        onClose={() => setIsDatasetPickerOpen(false)}
        title="Select simulation dataset"
        items={datasets}
        multiSelect={false}
        columns={datasetPickerColumns}
        onSaveSelected={importChatToDataset}
        saveLabel={chatImporting ? 'Importing...' : 'Import'}
        searchPlaceholder="Search datasets..."
        isLoading={datasetsLoading}
        disabled={datasetsLoading || chatImporting}
      />
    </div>
  );
};

function ChatMessages() {
  return (
    <ConfirmationProvider>
      <ChatMessagesContent />
    </ConfirmationProvider>
  );
}

export default ChatMessages;
